'use client'

import { InvitationButton } from '@/components/invitation-button'
import { LanguageSelector } from '@/components/language-selector'
import { FeatureLink } from '@/components/common/feature-link'
import { Logo } from '@/components/logo'
import { ProfileMenu } from '@/components/profile-menu'
import type { User } from '@/lib/types'
import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { cn } from '@/lib/utils'
import { DISCORD_URL, X_URL } from '@/lib/constant'
import ChangelogInbox from './change-log-inbox'
import Link from 'next/link'

export const HeaderV3 = ({
  user,
  disableLanguageSelector = false,
  initialVariant = 'light',
}: {
  user?: User | null
  disableLanguageSelector?: boolean
  initialVariant?: 'light' | 'dark'
}) => {
  const [scrolled, setScrolled] = useState(false)
  const t = useTranslations('home.header')

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [scrolled])

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div
      className={cn(
        'fixed w-full top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled
          ? 'bg-dark-bg/80 backdrop-blur-md shadow-lg'
          : 'bg-transparent border-transparent',
      )}
    >
      <div className="w-full px-4">
        <div className="flex items-center justify-between py-3 sm:py-4 mx-auto">
          <div className="flex items-center gap-4 sm:gap-6 lg:gap-8 flex-1 min-w-0">
            {/* Logo */}
            <div
              className="flex items-center cursor-pointer shrink-0"
              onClick={() => scrollToSection('hero')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  scrollToSection('hero')
                }
              }}
            >
              <Logo
                variant={scrolled ? 'light' : initialVariant}
                className="h-6 min-[700px]:h-8"
              />
            </div>

            <nav className="hidden min-[700px]:flex items-center gap-4 xl:gap-6">
              <a
                className={cn(
                  'text-sm xl:text-base transition-colors whitespace-nowrap',
                  scrolled || initialVariant === 'light'
                    ? 'text-white hover:text-white/80'
                    : 'text-black hover:text-black/80',
                )}
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('community')}
              </a>
              <Link
                className={cn(
                  'text-sm xl:text-base transition-colors whitespace-nowrap',
                  scrolled || initialVariant === 'light'
                    ? 'text-white hover:text-white/80'
                    : 'text-black hover:text-black/80',
                )}
                href="/pricing"
              >
                {t('pricing')}
              </Link>
              <FeatureLink
                className={cn(
                  'text-sm xl:text-base transition-colors whitespace-nowrap',
                  scrolled || initialVariant === 'light'
                    ? 'text-white hover:text-white/80'
                    : 'text-black hover:text-black/80',
                )}
              >
                {t('features')}
              </FeatureLink>
            </nav>
          </div>

          <div className="flex items-center gap-2 min-[700px]:gap-3">
            {!disableLanguageSelector && (
              <div className="hidden min-[700px]:block">
                <LanguageSelector
                  variant={
                    scrolled || initialVariant === 'light' ? 'dark' : 'light'
                  }
                />
              </div>
            )}

            <div className="flex items-center gap-1 min-[700px]:hidden">
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-8 h-8 bg-black/20 rounded-lg hover:bg-black/30 transition-colors"
              >
                {/* biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
                <svg
                  className="w-4 h-4 text-white"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.736 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
                </svg>
              </a>

              <a
                href={X_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-8 h-8 bg-black/20 rounded-lg hover:bg-black/30 transition-colors"
              >
                <svg
                  className="w-4 h-4 text-white"
                  aria-label="follow HeyBoss AI on X.com"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <title>follow HeyBoss AI on X.com</title>
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>

            {user && (
              <ChangelogInbox
                variant={
                  scrolled || initialVariant === 'light' ? 'dark' : 'light'
                }
              />
            )}

            {user && (
              <div className="hidden min-[700px]:flex">
                <InvitationButton
                  variant={
                    scrolled || initialVariant === 'light' ? 'dark' : 'light'
                  }
                />
              </div>
            )}

            <div className="flex-shrink-0">
              <ProfileMenu
                user={user || null}
                showSocialMediaLinks={false}
                transparent={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
